import type { Id } from '@contracts/common';
import {
  PERMISSION_LIST, PERMISSIONS, type ObjectRef, type Permission, type PermissionGrant, type PermissionMap,
  type ScopeObjectType,
} from '@contracts/rbac';

/**
 * The permission rules the server and the web share (plan Global Constraints, "Scope semantics"). Pure: no I/O,
 * no clock. The server builds `Principal.permissions` with `mergeGrants`; both sides ask it with the rest.
 */

const IMPLIES_VIEW: ReadonlySet<string> = new Set(['edit', 'delete', 'configure']);

const viewOf = (perm: Permission): Permission | null => {
  const view = `${PERMISSIONS[perm].module}.view`;
  return Object.hasOwn(PERMISSIONS, view) ? view as Permission : null;
};

/** In catalogue order, without repeats. */
const ordered = (set: ReadonlySet<Permission>): Permission[] => PERMISSION_LIST.filter((perm) => set.has(perm));

/** The permissions with `<module>.view` added for every `edit`, `delete` or `configure`; deduplicated, catalogue order. */
export function withImpliedView(perms: readonly Permission[]): Permission[] {
  const set = new Set<Permission>(perms);
  for (const perm of perms) {
    if (!IMPLIES_VIEW.has(PERMISSIONS[perm].action)) continue;
    const view = viewOf(perm);
    if (view !== null) set.add(view);
  }
  return ordered(set);
}

/** Unticking a module's `view` also unticks that module's `edit`, `delete` and `configure`; nothing else moves. */
export function dropDependents(perms: readonly Permission[], removedView: Permission): Permission[] {
  const module = PERMISSIONS[removedView].module;
  return perms.filter((perm) => perm !== removedView &&
    !(PERMISSIONS[perm].module === module && IMPLIES_VIEW.has(PERMISSIONS[perm].action)));
}

/** The object types a role's permissions can be limited to, each once, in catalogue order. */
export function limitableTypes(perms: readonly Permission[]): ScopeObjectType[] {
  const types: ScopeObjectType[] = [];
  for (const perm of PERMISSION_LIST) {
    const type = PERMISSIONS[perm].objectType;
    if (type !== null && perms.includes(perm) && !types.includes(type)) types.push(type);
  }
  return types;
}

/** One live assignment: its role's permissions and its scope. */
export type AssignmentSource = { permissions: readonly Permission[]; scope: 'all' | readonly ObjectRef[] };

const sameRef = (a: ObjectRef, b: ObjectRef): boolean => a.type === b.type && a.id === b.id;

/**
 * The permission map of a principal. Each source's permissions first gain their implied view; then an `all`
 * source grants each as `'all'`, and an `objects` source grants a company-wide permission (`objectType` null) as
 * `'all'` and a permission of type T as its objects of type T — none of type T grants nothing. Across sources
 * `'all'` wins, else the objects are unioned without repeats. The system role (`isAdmin`) holds everything at `'all'`.
 */
export function mergeGrants(sources: readonly AssignmentSource[], isAdmin: boolean): PermissionMap {
  if (isAdmin) {
    const all: PermissionMap = {};
    for (const perm of PERMISSION_LIST) all[perm] = 'all';
    return all;
  }
  const merged = new Map<Permission, 'all' | ObjectRef[]>();
  for (const source of sources) {
    for (const perm of withImpliedView(source.permissions)) {
      const type = PERMISSIONS[perm].objectType;
      const grant: 'all' | ObjectRef[] = source.scope === 'all' || type === null
        ? 'all'
        : source.scope.filter((ref) => ref.type === type).map((ref) => ({ type: ref.type, id: ref.id }));
      if (grant !== 'all' && grant.length === 0) continue;
      const held = merged.get(perm);
      if (held === 'all') continue;
      if (grant === 'all') {
        merged.set(perm, 'all');
        continue;
      }
      const list = held ?? [];
      for (const ref of grant) if (!list.some((h) => sameRef(h, ref))) list.push(ref);
      merged.set(perm, list);
    }
  }
  const map: PermissionMap = {};
  for (const perm of PERMISSION_LIST) {
    const grant = merged.get(perm);
    if (grant !== undefined) map[perm] = grant;
  }
  return map;
}

const held = (grant: PermissionGrant | undefined): boolean => grant === 'all' || (grant !== undefined && grant.length > 0);

/** Whether the map holds the permission at any scope. */
export function hasPermission(map: PermissionMap, perm: Permission): boolean {
  return held(map[perm]);
}

/** Without an object, whether the permission is held at `'all'`; with one, whether that object is covered. */
export function allows(map: PermissionMap, perm: Permission, object?: ObjectRef): boolean {
  const grant = map[perm];
  if (grant === 'all') return true;
  if (grant === undefined || object === undefined) return false;
  return grant.some((ref) => sameRef(ref, object));
}

/** `'all'`, the ids of the permission's object type it is limited to, or `'none'`. */
export function scopeOf(map: PermissionMap, perm: Permission): 'all' | Id[] | 'none' {
  const grant = map[perm];
  if (grant === 'all') return 'all';
  const type = PERMISSIONS[perm].objectType;
  const ids = (grant ?? []).filter((ref) => ref.type === type).map((ref) => ref.id);
  return ids.length === 0 ? 'none' : ids;
}

/** Whether the actor holds every one of the permissions at `'all'`; an objects grant does not count. */
export function holdsAllOf(actor: PermissionMap, perms: readonly Permission[]): boolean {
  return perms.every((perm) => actor[perm] === 'all');
}
